/**
 * The AI window's top part: which AI service to ask. Choosing a known service fills the format
 * and address; the model can be any the service offers. The key is sent once to the core, which
 * keeps it in its data folder and only shows its last characters back.
 */
import { Show, createEffect, createSignal, type JSX } from "solid-js";
import type { AiFormat } from "@protocol/AiFormat";
import { useApp } from "../../app/context";
import { AI_PROVIDERS, CUSTOM, providerFor } from "../../shared/aiProviders";

/** The service form, Save and Test. */
export function ServiceSection(): JSX.Element {
  const { ai } = useApp();
  const [provider, setProvider] = createSignal("custom");
  const [format, setFormat] = createSignal<AiFormat>("openai");
  const [baseUrl, setBaseUrl] = createSignal("");
  const [model, setModel] = createSignal("");
  const [key, setKey] = createSignal("");

  // Follow the core's settings (they also change when another window or the CLI saves them).
  createEffect(() => {
    const s = ai.settings();
    if (!s) return;
    setFormat(s.format);
    setBaseUrl(s.base_url);
    setModel(s.model);
    setProvider(s.base_url ? providerFor(s.format, s.base_url).id : "custom");
  });

  const chosen = () => AI_PROVIDERS.find((p) => p.id === provider()) ?? CUSTOM;
  /** Picks a known service: its format, address and a model to start from. */
  const pick = (id: string) => {
    setProvider(id);
    const p = AI_PROVIDERS.find((x) => x.id === id);
    if (!p || p.id === "custom") return;
    setFormat(p.format);
    setBaseUrl(p.baseUrl);
    setModel(p.model);
  };
  /** Saves the form; an empty key field keeps the saved key. */
  const save = async () => {
    const typed = key().trim();
    if (await ai.save({ format: format(), base_url: baseUrl(), model: model(), ...(typed ? { api_key: typed } : {}) })) setKey("");
  };
  const keyPlaceholder = () => {
    const s = ai.settings();
    if (s?.key_set) return `saved${s.key_hint ? ` (${s.key_hint})` : ""}: leave empty to keep it`;
    return chosen().needsKey ? "paste your API key" : "not needed";
  };

  return (
    <section class="grp">
      <h3>AI service</h3>
      <label class="fl">
        <span>Service</span>
        <select class="select" id="ai-provider" value={provider()} onChange={(e) => pick(e.currentTarget.value)}>
          {AI_PROVIDERS.map((p) => (
            <option value={p.id}>{p.name}</option>
          ))}
        </select>
      </label>
      <Show when={provider() === "custom"}>
        <label class="fl">
          <span>Format</span>
          <select class="select" id="ai-format" value={format()} onChange={(e) => setFormat(e.currentTarget.value as AiFormat)}>
            <option value="openai">OpenAI-compatible</option>
            <option value="anthropic">Anthropic</option>
          </select>
        </label>
      </Show>
      <label class="fl">
        <span>Address</span>
        <input class="field mono" id="ai-base-url" placeholder="https://…/v1" value={baseUrl()} onInput={(e) => setBaseUrl(e.currentTarget.value)} />
      </label>
      <label class="fl">
        <span>Model</span>
        <input class="field mono" id="ai-model" placeholder="model name" value={model()} onInput={(e) => setModel(e.currentTarget.value)} />
      </label>
      <label class="fl">
        <span>API key</span>
        <input
          class="field mono"
          id="ai-key"
          type="password"
          autocomplete="off"
          placeholder={keyPlaceholder()}
          value={key()}
          onInput={(e) => setKey(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && void save()}
        />
      </label>
      <p class="small muted">{chosen().hint} The key stays with the app's core, in its data folder; it is never shown again nor saved in projects.</p>
      <div class="btnrow">
        <button type="button" class="btn primary" id="ai-save" disabled={!!ai.busy() || !baseUrl().trim() || !model().trim()} onClick={() => void save()}>
          {ai.busy() === "save" ? "Saving…" : "Save"}
        </button>
        <button type="button" class="btn" id="ai-test" disabled={!!ai.busy() || !ai.ready()} onClick={() => void ai.test()}>
          {ai.busy() === "test" ? "Testing…" : "Test"}
        </button>
        <Show when={ai.settings()?.key_set}>
          <button
            type="button"
            class="btn"
            id="ai-forget-key"
            disabled={!!ai.busy()}
            onClick={() => void ai.save({ format: format(), base_url: baseUrl(), model: model(), api_key: "" })}
          >
            Forget key
          </button>
        </Show>
      </div>
      <Show when={ai.checked()}>
        {(c) => (
          <p class="small ok" data-ai-checked>
            ✔ The service answered in {(c().ms / 1000).toFixed(1)} s: “{c().reply}”
          </p>
        )}
      </Show>
    </section>
  );
}
